import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, share, switchMap } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { SharedActions } from '@states/shared/shared.action-types';
import { PrivateStorageActions } from '@states/private-storage/private-storage.action-types';
import { PrivateStorageModel } from '@models/private-storage.model';
import { PrivateStorageService } from '../../services/private-storage.service';
import { ThumbnailsService } from '../../development/thumbnails.service';
import { UtilsService } from '../../edge/utils.service';

const ENTITY_NAME = 'Private storage';

@Injectable()
export class PrivateStorageEffects {

  public getOptions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PrivateStorageActions.getOptions),
      mergeMap(() => {
        return this.privateStorageService.getOptions(true)
          .pipe(
            switchMap(res => {
              return [
                PrivateStorageActions.getOptionsSuccess({ documents: res as PrivateStorageModel.Document[] }),
              ];
            }),
            catchError(response => {
              const err = this.utilsService.errMessage(response);
              return [
                PrivateStorageActions.getOptionsFail(),
                SharedActions.showMessage({ error: err }),
              ];
            }),
          );
      }),
    ),
  );

  public createOption$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PrivateStorageActions.createOption),
      switchMap(({ body }) => {
        return this.privateStorageService.createOption(body)
          .pipe(
            switchMap(res => {
              this.thumbnailsService.reloadPrivateStorageMap();
              return [
                PrivateStorageActions.createOptionSuccess({ document: res }),
                PrivateStorageActions.getOptions(),
                PrivateStorageActions.getOrgDefault(),
                SharedActions.showMessage({ success: this.translateService.instant('TOASTS.ENTITY_CREATED_SUCCESS', { entity: ENTITY_NAME }) }),
              ];
            }),
            catchError(response => {
              const err = this.utilsService.errMessage(response);
              return [
                PrivateStorageActions.createOptionFail(),
                SharedActions.showMessage({ error: err }),
              ];
            }),
          );
      }),
      share(),
    ),
  );

  public updateOption$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PrivateStorageActions.updateOption),
      switchMap(({ id, body }) => {
        return this.privateStorageService.updateOption(id, body)
          .pipe(
            switchMap(res => {
              this.thumbnailsService.reloadPrivateStorageMap();
              return [
                PrivateStorageActions.updateOptionSuccess({ document: res }),
                PrivateStorageActions.getOptions(),
                PrivateStorageActions.getOrgDefault(),
                SharedActions.showMessage({ success: this.translateService.instant('TOASTS.ENTITY_UPDATED_SUCCESS', { entity: ENTITY_NAME }) }),
              ];
            }),
            catchError(response => {
              const err = this.utilsService.errMessage(response);
              return [
                PrivateStorageActions.updateOptionFail(),
                SharedActions.showMessage({ error: err }),
              ];
            }),
          );
      }),
      share(),
    ),
  );

  public deleteOption$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PrivateStorageActions.deleteOption),
      mergeMap(({ id }) => {
        return this.privateStorageService.deleteOption(id)
          .pipe(
            switchMap(() => {
              this.thumbnailsService.reloadPrivateStorageMap();
              return [
                PrivateStorageActions.deleteOptionSuccess({ id }),
                PrivateStorageActions.getOptions(),
                PrivateStorageActions.getOrgDefault(),
                SharedActions.showMessage({ success: this.translateService.instant('TOASTS.ENTITY_DELETED_SUCCESS', { entity: ENTITY_NAME }) }),
              ];
            }),
            catchError(response => {
              const err = this.utilsService.errMessage(response);
              return [
                PrivateStorageActions.deleteOptionFail({ id }),
                SharedActions.showMessage({ error: err }),
              ];
            }),
          );
      }),
    ),
  );

  public getOrgDefault$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PrivateStorageActions.getOrgDefault),
      switchMap(() => {
        return this.privateStorageService.getOrgDefault()
          .pipe(
            switchMap(res => {
              return [
                PrivateStorageActions.getOrgDefaultSuccess({ response: res }),
              ];
            }),
            catchError(response => {
              const err = this.utilsService.errMessage(response);
              return [
                SharedActions.showMessage({ error: err }),
              ];
            }),
          );
      }),
    ),
  );

  public setOrgDefault$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PrivateStorageActions.setOrgDefault),
      switchMap(({ privateStorageId }) => {
        return this.privateStorageService.setOrgDefault(privateStorageId)
          .pipe(
            switchMap(res => {
              this.thumbnailsService.reloadPrivateStorageMap();
              return [
                PrivateStorageActions.setOrgDefaultSuccess({ response: res }),
                PrivateStorageActions.getOptions(),
                PrivateStorageActions.getOrgDefault(),
                SharedActions.showMessage({ success: this.translateService.instant('TOASTS.ENTITY_UPDATED_SUCCESS', { entity: ENTITY_NAME }) }),
              ];
            }),
            catchError(response => {
              const err = this.utilsService.errMessage(response);
              return [
                PrivateStorageActions.setOrgDefaultFail(),
                SharedActions.showMessage({ error: err }),
              ];
            }),
          );
      }),
      share(),
    ),
  );

  public clearOrgDefault$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PrivateStorageActions.clearOrgDefault),
      switchMap(() => {
        return this.privateStorageService.clearOrgDefault()
          .pipe(
            switchMap(() => {
              this.thumbnailsService.reloadPrivateStorageMap();
              return [
                PrivateStorageActions.clearOrgDefaultSuccess(),
                PrivateStorageActions.getOptions(),
                PrivateStorageActions.getOrgDefault(),
                SharedActions.showMessage({ success: this.translateService.instant('TOASTS.ENTITY_UPDATED_SUCCESS', { entity: ENTITY_NAME }) }),
              ];
            }),
            catchError(response => {
              const err = this.utilsService.errMessage(response);
              return [
                PrivateStorageActions.clearOrgDefaultFail(),
                SharedActions.showMessage({ error: err }),
              ];
            }),
          );
      }),
      share(),
    ),
  );

  public getLocationSelection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PrivateStorageActions.getLocationSelection),
      switchMap(({ locationId }) => {
        return this.privateStorageService.getLocationSelection(locationId)
          .pipe(
            switchMap(res => {
              return [
                PrivateStorageActions.getLocationSelectionSuccess({ selection: res }),
              ];
            }),
            catchError(response => {
              const err = this.utilsService.errMessage(response);
              return [
                PrivateStorageActions.getLocationSelectionFail(),
                SharedActions.showMessage({ error: err }),
              ];
            }),
          );
      }),
    ),
  );

  public setLocationSelection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PrivateStorageActions.setLocationSelection),
      switchMap(({ locationId, body }) => {
        return this.privateStorageService.setLocationSelection(locationId, body)
          .pipe(
            switchMap(res => {
              this.thumbnailsService.reloadPrivateStorageMap();
              return [
                PrivateStorageActions.setLocationSelectionSuccess({ response: res }),
                PrivateStorageActions.getLocationSelection({ locationId }),
                SharedActions.showMessage({ success: this.translateService.instant('TOASTS.ENTITY_UPDATED_SUCCESS', { entity: ENTITY_NAME }) }),
              ];
            }),
            catchError(response => {
              const err = this.utilsService.errMessage(response);
              return [
                PrivateStorageActions.setLocationSelectionFail(),
                SharedActions.showMessage({ error: err }),
              ];
            }),
          );
      }),
      share(),
    ),
  );

  public testConnection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PrivateStorageActions.testConnection),
      switchMap(({ body }) => {
        return this.privateStorageService.testConnection(body)
          .pipe(
            switchMap(response => {
              const message = response.connected
                ? { success: this.translateService.instant('PRIVATE_STORAGE.TEST.SUCCESS') }
                : { error: this.translateService.instant('PRIVATE_STORAGE.TEST.FAILED') };
              return [
                PrivateStorageActions.testConnectionSuccess({ response }),
                SharedActions.showMessage(message),
              ];
            }),
            catchError(response => {
              const err = this.utilsService.errMessage(response);
              return [
                PrivateStorageActions.testConnectionFail(),
                SharedActions.showMessage({ error: err }),
              ];
            }),
          );
      }),
      share(),
    ),
  );

  constructor(private privateStorageService: PrivateStorageService,
              private actions$: Actions,
              private utilsService: UtilsService,
              private translateService: TranslateService,
              private thumbnailsService: ThumbnailsService) {
  }

}
